import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Legal from "@/pages/Legal";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import { BlogList, BlogPost } from "@/pages/Blog";
import { WhatsAppButton, ScrollManager } from "@/components/site";
import { SeoProvider, SiteHead } from "@/lib/seo";
import { LoginPage, RegisterPage, ForgotPage, ResetPage, VerifyEmailPage } from "@/pages/AuthPages";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/app/AppLayout";
import Overview from "@/app/Overview";
import ToolPage from "@/app/ToolPage";
import Projects from "@/app/Projects";
import Models from "@/app/Models";
import Subscription from "@/app/Subscription";
import Billing from "@/app/Billing";
import Settings from "@/app/Settings";
import Admin from "@/admin/Admin";

function Protected({ children, admin }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-neutral-400" data-testid="auth-loading">…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/legal/:doc" element={<Legal />} />
      <Route path="/hakkimizda" element={<About />} />
      <Route path="/iletisim" element={<Contact />} />
      <Route path="/blog" element={<BlogList />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPage />} />
      <Route path="/reset-password" element={<ResetPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Overview />} />
        <Route path="hero" element={<ToolPage tool="hero" />} />
        <Route path="model" element={<ToolPage tool="model" />} />
        <Route path="lifestyle" element={<ToolPage tool="lifestyle" />} />
        <Route path="dimensions" element={<ToolPage tool="dimensions" />} />
        <Route path="projects" element={<Projects />} />
        <Route path="models" element={<Models />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <SeoProvider>
            <AppRouter />
            <SiteHead />
            <ScrollManager />
            <WhatsAppButton />
            <Toaster richColors theme="dark" position="top-center" />
          </SeoProvider>
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}
